import { Flex, Typography, Skeleton } from 'antd';
import { Card } from 'antd';
import { ResponsiveLine } from '@nivo/line';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect } from 'react';
import { useState } from 'react';

import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import updateLocale from 'dayjs/plugin/updateLocale';
import type { ParsedChartItem } from '../../interface/campaign-analytics';
import { countingNumberOfDays } from '../../helpers/mixins';

dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.extend(updateLocale);

const isSundayFirstTimezone = (timezoneKey: string) => {
  const sundayFirstTimezoneKeys = [
    'Pacific',
    'Mountain',
    'Central',
    'Eastern',
    'Tokyo',
    'China',
    'Taipei',
    'Israel',
    'Egypt',
    'SouthAfrica',
    'Brazil',
    'Philippine',
  ];
  return sundayFirstTimezoneKeys.some((tz) => timezoneKey.includes(tz));
};

const getMaxValueLength = (chartData: ParsedChartItem[]) => {
  const maxValue = Math.max(...chartData.flatMap((series) => series.data.map((point) => point.y as number)));
  return maxValue.toString().length;
};

export const AnalyticsCard = ({
  children,
  title,
  extra,
  chartData,
  dates,
  interval,
  timezone,
  hideChart = false,
  loading = false,
}: {
  children?: React.ReactNode;
  title: string;
  extra: React.ReactNode;
  chartData?: ParsedChartItem[];
  dates?: [Dayjs | null, Dayjs | null];
  interval?: string;
  timezone: string;
  hideChart?: boolean;
  loading?: boolean;
}) => {
  const [format, setFormat] = useState<string>('MMM-DD');
  const [tickValues, setTickValues] = useState<string | Dayjs[]>('every day');

  const customEveryWeekTickValues = (dates: [Dayjs | null, Dayjs | null]) => {
    const startDate = dates?.[0] || dayjs();
    const endDate = dates?.[1] || dayjs();
    const allWeeks = [];
    let currentDate = startDate.startOf('week');

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      allWeeks.push(currentDate);
      currentDate = currentDate.add(1, 'week');
    }
    return allWeeks;
  };

  useEffect(() => {
    const numberOfDays = countingNumberOfDays(dates?.[0] || dayjs(), dates?.[1] || dayjs());

    const formatConfig = {
      Daily: {
        format: 'MMM-DD',
        tickValues: numberOfDays >= 30 ? customEveryWeekTickValues(dates || [dayjs(), dayjs()]) : 'every day',
      },
      Weekly: {
        format: 'MMM-DD',
        tickValues: numberOfDays <= 90 ? customEveryWeekTickValues(dates || [dayjs(), dayjs()]) : 'every month',
      },
      Monthly: {
        format: 'MMM-DD',
        tickValues: numberOfDays <= 365 ? 'every month' : 'every 3 months',
      },
      Yearly: {
        format: numberOfDays <= 365 ? 'MMM-DD' : 'YYYY',
        tickValues: numberOfDays <= 365 ? 'every month' : 'every year',
      },
    };

    const config = formatConfig[interval as keyof typeof formatConfig];

    if (config) {
      setFormat(config.format);
      setTickValues(config.tickValues);
      console.log(config.tickValues);
    }
  }, [dates, interval]);

  useEffect(() => {
    dayjs.updateLocale('en', {
      weekStart: isSundayFirstTimezone(timezone) ? 0 : 1,
    });
  }, [timezone]);

  return (
    <Card
      style={{
        backgroundColor: '#FAFAFA',
      }}
      title={
        <Flex
          wrap
          gap={16}
          align="center"
        >
          <span className="analytics-card-title">{title}</span>
        </Flex>
      }
      extra={extra}
    >
      <Flex
        gap={16}
        wrap
      >
        {children}
      </Flex>
      {!hideChart &&
        (loading ? (
          <div
            style={{
              width: '100%',
              height: 450,
              borderRadius: '8px',
              border: '1px solid #E9EAEB',
              backgroundColor: '#fff',
              padding: '16px',
              marginTop: '32px',
            }}
          >
            <Skeleton
              active
              paragraph={{ rows: 10 }}
            />
          </div>
        ) : chartData && chartData.length > 0 ? (
          <Flex justify="space-between">
            <div
              style={{
                width: '100%',
                height: 450,
                borderRadius: '8px',
                border: '1px solid #E9EAEB',
                backgroundColor: '#fff',

                padding: '16px',
                marginTop: '32px',
              }}
            >
              <ResponsiveLine
                data={chartData}
                margin={{
                  top: 30,
                  right: 30,
                  bottom: 80,
                  left: getMaxValueLength(chartData) * 8 + 10,
                }}
                xScale={{
                  type: 'time',
                  precision: interval === 'Weekly' ? 'day' : undefined,
                }}
                yScale={{
                  min: Math.min(0, ...chartData.map((series) => Math.min(...series.data.map((point) => point.y as number)))),
                  max: 'auto',
                  type: 'linear',
                  nice: true,
                }}
                curve="monotoneX"
                enableGridX={false}
                enableGridY={true}
                colors={chartData.map((item) => item.color)}
                pointSize={7}
                pointColor={{ from: 'color' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                enableTouchCrosshair={true}
                motionConfig="default"
                lineWidth={3}
                useMesh={true}
                theme={{
                  background: '#ffffff',
                  grid: {
                    line: {
                      strokeDasharray: '5 5',
                      stroke: '#e0e0e0',
                    },
                  },
                }}
                axisBottom={{
                  tickRotation: 0,
                  format: (value) => {
                    const date = dayjs(value);
                    return date.format(format);
                  },
                  tickValues: tickValues,
                }}
                axisLeft={{
                  format: (value) => {
                    // Tam sayı formatlaması
                    return Number.isInteger(value) ? value.toString() : '';
                  },
                }}
                legends={[
                  {
                    anchor: 'bottom',
                    direction: 'row',
                    justify: false,
                    itemsSpacing: 40,
                    itemWidth: 200,
                    itemHeight: 15,
                    itemDirection: 'top-to-bottom',
                    itemTextColor: '#868585',
                    itemOpacity: 1,
                    symbolSize: 12,
                    symbolShape: 'square',
                    translateY: 60,
                  },
                ]}
                enableSlices="x"
                sliceTooltip={({ slice }) => {
                  const date = dayjs(slice.points[0].data.xFormatted);

                  const endOfWeek = date.endOf('week');
                  const endOfMonth = date.endOf('month');
                  const endOfYear = date.endOf('year');

                  return (
                    <div className="analytics-card-tooltip">
                      <div className="analytics-card-tooltip-title">
                        {interval === 'Weekly' && (
                          <span className="analytics-card-tooltip-title-week">{`${date.format('MMM DD')} - ${endOfWeek.format(
                            'MMM DD, YYYY'
                          )}`}</span>
                        )}
                        {interval === 'Monthly' && (
                          <span className="analytics-card-tooltip-title-month">
                            {`${date.format('MMM DD')} - ${endOfMonth.format('MMM DD, YYYY')}`}
                          </span>
                        )}
                        {interval === 'Yearly' && (
                          <span className="analytics-card-tooltip-title-year">{`${date.format('MMM DD')} - ${endOfYear.format(
                            'MMM DD, YYYY'
                          )}`}</span>
                        )}
                        {interval === 'Daily' && <span className="analytics-card-tooltip-title-day">{`${date.format('MMM DD, YYYY')}`}</span>}
                      </div>
                      {slice.points.map((point: any) => (
                        <div className="analytics-card-tooltip-item">
                          <div className="analytics-card-tooltip-item-icon-wrapper">
                            <div
                              className="analytics-card-tooltip-item-icon"
                              style={{ backgroundColor: point.seriesColor }}
                            />
                            <Typography.Text className="analytics-card-tooltip-item-label">{point.seriesId}</Typography.Text>
                          </div>
                          <Typography.Text className="analytics-card-tooltip-item-value">{point.data.yFormatted}</Typography.Text>
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
            </div>
          </Flex>
        ) : (
          <Flex
            justify="center"
            align="center"
            style={{
              width: '100%',
              height: 450,
              borderRadius: '8px',
              border: '1px solid #E9EAEB',
              backgroundColor: '#fff',
              marginTop: '32px',
            }}
          >
            <Flex
              vertical
              align="center"
              gap={16}
              style={{
                textAlign: 'center',
                padding: '40px',
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '0px',
                }}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="#bfbfbf"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <Typography.Title
                  level={4}
                  style={{
                    color: '#8c8c8c',
                    marginBottom: '8px',
                    fontWeight: '500',
                  }}
                >
                  No Data Found
                </Typography.Title>
                <Typography.Text
                  style={{
                    color: '#8c8c8c',
                    fontSize: '14px',
                  }}
                >
                  No data available for the selected date range.
                </Typography.Text>
              </div>
            </Flex>
          </Flex>
        ))}
    </Card>
  );
};
